import { WorkspaceService } from '@/api';
import Breadcrumb from '@/components/breadcrumb';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useStore } from '@nanostores/react';
import { useQuery } from '@tanstack/react-query';
import { PlusIcon } from 'lucide-react';
import { AddNewWorkspaceCollaborator } from './components/AddNewWorkspaceCollaborator';

export type WorkspaceCollaboratorsProps = {
  organizationId: string;
  workspaceId: string;
};

export const WorkspaceCollaborators = ({
  organizationId,
  workspaceId,
}: WorkspaceCollaboratorsProps) => {
  const org = useStore($org);

  const ws = useStore($workspace);

  // current logged in user
  const user = useStore($user);

  // query to get the collaborators of the workspace
  const collaborators = useQuery({
    queryKey: ['getWorkspaceCollaborators', workspaceId],
    enabled: !!workspaceId,
    queryFn: async () => {
      const res = await WorkspaceService.getCollaborators(workspaceId);

      return res;
    },
  });

  const [showDrawer, setShowDrawer] = useState(false);

  const pages = [
    {
      label: org?.name || 'Organizations',
      href: '/organizations',
    },
    {
      label: ws?.workspace.name || 'Workspaces',
      href: `/organization/${organizationId}/workspaces`,
    },
    {
      label: 'Collaborators',
      href: `/organization/${organizationId}/workspace/${workspaceId}/collaborators`,
    },
  ];

  return (
    <div>
      {/* Breadcrumb */}
      <h3 className="m-0 flex-1 p-0">
        <Breadcrumb pages={pages} />
      </h3>

      {/* Page Header with actions */}
      <div className=" mt-2 flex items-center justify-between space-y-2">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">
            Workspace collaborators
          </h2>
          <p className="text-muted-foreground">
            Manage the collaborators of workspace:{' '}
            <span className="font-semibold">{ws?.workspace.name}</span>
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <Button onClick={() => setShowDrawer(true)}>
            <PlusIcon className="mr-2 h-4 w-4" /> Add new collaborator
          </Button>
        </div>
      </div>

      {/* Collaborators and invites */}
      <div className="mt-4 max-w-4xl">
        {/* Collaborators list */}
        <Card>
          <CardHeader>
            <CardTitle>Current Members</CardTitle>
          </CardHeader>
          <CardContent className="grid gap-6">
            {collaborators.isLoading && <p>Loading...</p>}
            {collaborators.isError && (
              <p>Error: {collaborators.error.message}</p>
            )}

            {collaborators.data?.collaborators.map(c => (
              <div
                key={c.collaborator?.id}
                className="flex items-center justify-between space-x-4"
              >
                <div className="flex items-center space-x-4">
                  <Avatar className="">
                    <AvatarImage
                      src={c.collaborator?.photoUrl || ''}
                      alt="Profile menu"
                    />
                    <AvatarFallback>
                      {c.collaborator?.name.slice(0, 2).toLocaleUpperCase() ||
                        'A'}
                    </AvatarFallback>
                  </Avatar>
                  <div>
                    <p className="text-sm font-medium leading-none">
                      {c.collaborator?.name || 'Name not found'}{' '}
                      {user?.uid === c.collaborator?.id && (
                        <Badge variant="outline">You</Badge>
                      )}
                      {/* TODO: Add role badge here */}
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {c.collaborator?.email || 'Email not found'}
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  {/* TODO: Add API to remove/edit the collaborator */}
                  {/* Remove Member button */}
                  <Button variant="destructive" disabled={true}>
                    Remove
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* Invites list */}
        {collaborators.data?.invites.length &&
        collaborators.data.invites.length > 0 ? (
          <Card className="mt-4">
            <CardHeader>
              <CardTitle>Current Invites</CardTitle>
            </CardHeader>
            <CardContent className="grid gap-1">
              {collaborators.data.invites.map(i => (
                <div
                  key={i.id}
                  className="flex items-center justify-between space-x-4 rounded-lg p-4 transition-colors duration-200 ease-in-out hover:bg-accent"
                >
                  <div className="">
                    <div className="text-sm font-medium leading-none">
                      {i.name || 'Name not found'}{' '}
                      <Badge variant="outline">{i.role}</Badge>
                    </div>
                    <p className="text-sm text-muted-foreground">
                      {i.email || 'Email not found'}
                    </p>
                  </div>

                  {/* TODO: Add a button to cancel the invite */}
                  {/* <div className="flex gap-4">
                      <Button variant="destructive">Cancel</Button>
                    </div> */}
                </div>
              ))}
            </CardContent>
          </Card>
        ) : null}
      </div>

      {/* Create New Organization Drawer */}
      <AddNewWorkspaceCollaborator
        open={showDrawer}
        setOpen={setShowDrawer}
        workspaceId={workspaceId}
      />
    </div>
  );
};
